import type { AgentApiAnalysisSalesSummary } from '@/types/serializers';
import DeltaIndicator from '../DeltaIndicator';
import InsightMetricSelect from '../InsightMetricSelect';
import { formatYen } from './salesChartUtils';

interface Props {
  summary: AgentApiAnalysisSalesSummary;
  insightMetric: 'lead_time' | 'sales';
  onMetricChange: (value: 'lead_time' | 'sales') => void;
}

/**
 * 売上ビューの上部サマリーカード。
 * 売上合計（見込み含む）の大カードと、成約数 / 平均成約日数 / 決定売上 のサブカードを表示する。
 */
export default function SalesSummaryCard({ summary, insightMetric, onMetricChange }: Props) {
  const isEmpty = summary.total_contracts === 0 && summary.total_sales === 0;

  return (
    <div className="flex flex-col gap-sm">
      <InsightMetricSelect value={insightMetric} onChange={onMetricChange}>
        <div className="flex flex-col items-start gap-xs">
          <p className="m-0 text-heading-sm text-main">売上合計（見込み含む）</p>
          <div className="flex items-end gap-xs">
            <span className="text-heading-xl text-main">
              {isEmpty ? '-' : formatYen(summary.total_sales)}
            </span>
            {!isEmpty && (
              <DeltaIndicator
                delta={summary.total_sales_delta}
                positiveIsGood
                suffix=""
                formatValue={formatYen}
              />
            )}
          </div>
        </div>
      </InsightMetricSelect>

      <div className="flex flex-wrap gap-sm">
        <div className="flex h-[75px] min-w-[160px] items-center rounded-sm border-l-4 border-solid border-primary bg-white px-xl py-sm shadow-layer1">
          <div className="flex flex-col items-start gap-xxs">
            <p className="m-0 text-heading-xxs text-main">成約数</p>
            <div className="flex items-end gap-xs">
              <span className="text-heading-md text-main">
                {isEmpty ? '-' : summary.total_contracts}件
              </span>
              {!isEmpty && (
                <DeltaIndicator delta={summary.total_contracts_delta} positiveIsGood suffix="" />
              )}
            </div>
          </div>
        </div>

        <div className="flex h-[75px] min-w-[160px] items-center rounded-sm border-l-4 border-solid border-primary bg-white px-xl py-sm shadow-layer1">
          <div className="flex flex-col items-start gap-xxs">
            <p className="m-0 text-heading-xxs text-main">平均成約日数</p>
            <div className="flex items-end gap-xs">
              <span className="text-heading-md text-main">
                {isEmpty ? '-' : summary.average_contract_days}日
              </span>
              {!isEmpty && (
                <DeltaIndicator
                  delta={summary.average_contract_days_delta}
                  positiveIsGood={false}
                  suffix="日"
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex h-[75px] min-w-[200px] items-center rounded-sm border-l-4 border-solid border-primary bg-white px-xl py-sm shadow-layer1">
          <div className="flex flex-col items-start gap-xxs">
            <p className="m-0 text-heading-xxs text-main">決定売上</p>
            <div className="flex items-end gap-xs">
              <span className="text-heading-md text-main">
                {isEmpty ? '-' : formatYen(summary.confirmed_sales)}
              </span>
              {!isEmpty && (
                <DeltaIndicator
                  delta={summary.confirmed_sales_delta}
                  positiveIsGood
                  suffix=""
                  formatValue={formatYen}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
